import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, Store, GraduationCap, Code2, CheckCircle2, Boxes, Sparkles, Map as MapIcon,
  BarChart3, Users, BookOpen, Briefcase, Monitor, Zap,
} from "lucide-react";
import Layout, { Breadcrumb } from "@/components/Layout";
import SEO from "@/components/SEO";
import { useT } from "@/lib/i18n";
import { pageOg } from "@/lib/cdn";

const SECTIONS = [
  { id: "wine-shops", tKey: "pro.shops.title", Icon: Store },
  { id: "wine-schools", tKey: "pro.schools.title", Icon: GraduationCap },
  { id: "professionals", tKey: "pro.pros.title", Icon: Code2 },
];

// Sticky sub-navigation that highlights the active anchor while scrolling.
const SubNav = ({ t, active }) => (
  <div className="sticky top-[72px] z-30 bg-white/90 backdrop-blur-md border-b border-[color:var(--gv-line)]">
    <div className="max-w-7xl mx-auto px-6 lg:px-10">
      <nav className="flex items-center gap-1 overflow-x-auto py-3" aria-label="Pro sections">
        {SECTIONS.map(({ id, tKey, Icon }) => {
          const isActive = active === id;
          return (
            <a
              key={id}
              href={`#${id}`}
              data-testid={`pro-subnav-${id}`}
              className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-[5px] text-[13px] whitespace-nowrap transition-colors ${
                isActive
                  ? "bg-[color:var(--gv-pink)]/8 text-[color:var(--gv-pink)] border border-[color:var(--gv-pink)]/40"
                  : "text-[color:var(--gv-ink)]/70 border border-transparent hover:text-[color:var(--gv-pink)]"
              }`}
            >
              <Icon size={13} />
              {t(tKey)}
            </a>
          );
        })}
        <a
          href="#work-with-us"
          data-testid="pro-subnav-work"
          className="ml-auto inline-flex items-center gap-1.5 px-3 py-2 text-[13px] text-[color:var(--gv-pink)]"
        >
          {t("pro.cta")} <ArrowRight size={13} />
        </a>
      </nav>
    </div>
  </div>
);

const SegmentSection = ({ id, Icon, eyebrow, title, desc, features, cta, imageSide = "right", imageUrl, bg }) => (
  <section id={id} className={`scroll-mt-[152px] py-20 md:py-28 ${bg ? "bg-[color:var(--gv-mist)]" : ""}`}>
    <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
      <div className={`lg:col-span-6 reveal ${imageSide === "left" ? "order-2" : ""}`}>
        <div className="eyebrow mb-4 inline-flex items-center gap-2"><Icon size={14} /> {eyebrow}</div>
        <h2 className="font-display text-[32px] md:text-[44px] leading-[1.1] text-[color:var(--gv-ink)] mb-5">{title}</h2>
        <p className="text-[16px] leading-[1.7] text-[color:var(--gv-ink)]/75 max-w-lg mb-7">{desc}</p>
        <ul className="space-y-3 mb-8">
          {features.map((f) => (
            <li key={f.title} className="flex items-start gap-3">
              <f.Icon size={17} className="mt-0.5 text-[color:var(--gv-pink)]" />
              <div>
                <div className="text-[14.5px] font-medium text-[color:var(--gv-ink)]">{f.title}</div>
                <div className="text-[13px] text-[color:var(--gv-ink)]/65">{f.text}</div>
              </div>
            </li>
          ))}
        </ul>
        <a href="#work-with-us" className="btn-primary" data-testid={`${id}-cta`}>
          {cta} <ArrowRight size={16} />
        </a>
      </div>

      <div className={`lg:col-span-6 reveal ${imageSide === "left" ? "order-1" : ""}`}>
        <div className="rounded-[6px] overflow-hidden border border-[color:var(--gv-line)] shadow-[0_30px_80px_-40px_rgba(46,80,103,0.3)] bg-[color:var(--gv-mist)]">
          <img
            src={imageUrl}
            alt=""
            className="w-full h-[420px] object-cover"
            loading="lazy"
          />
        </div>
      </div>
    </div>
  </section>
);

const ProPage = () => {
  const t = useT();
  const [active, setActive] = useState("wine-shops");
  const sectionRefs = useRef([]);

  // Track the section currently centered in the viewport to highlight sub-nav.
  useEffect(() => {
    const elements = SECTIONS.map((s) => document.getElementById(s.id)).filter(Boolean);
    sectionRefs.current = elements;
    if (!elements.length) return;

    const io = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting);
        if (visible.length) {
          const top = visible.sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
          if (top.target.id) setActive(top.target.id);
        }
      },
      { rootMargin: "-30% 0px -55% 0px", threshold: [0, 0.25, 0.5, 0.75, 1] }
    );
    elements.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  return (
    <Layout testId="page-pro">
      <SEO
        title={`geoVINUM for professionals`}
        description="Solutions for wine shops, wine schools and professionals — maps, iframes, APIs and widgets to sell, teach and grow."
        image={pageOg("pro")}
      />

      {/* Hero */}
      <section className="pt-10 md:pt-16 pb-10">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <Breadcrumb items={[{ to: "/", label: t("common.home") }, { label: t("nav.pro") }]} />
          <div className="mt-5 max-w-3xl">
            <div className="eyebrow mb-4">{t("pro.hero.eyebrow")}</div>
            <h1 className="font-display text-[42px] md:text-[64px] leading-[1.05] text-[color:var(--gv-ink)] mb-4" data-testid="pro-hero-title">
              {t("pro.hero.h1")} <span className="italic text-[color:var(--gv-pink)]">{t("pro.hero.h1_italic")}</span>
            </h1>
            <p className="text-[16.5px] leading-[1.7] text-[color:var(--gv-ink)]/75 max-w-xl">{t("pro.hero.sub")}</p>
          </div>
        </div>
      </section>

      <SubNav t={t} active={active} />

      {/* Segments */}
      <SegmentSection
        id="wine-shops"
        Icon={Store}
        eyebrow={t("pro.shops.title")}
        title={<>{t("pro.shops.headline_a")} <span className="italic text-[color:var(--gv-pink)]">{t("pro.shops.headline_b")}</span> {t("pro.shops.headline_c")}</>}
        desc={t("pro.shops.desc")}
        features={[
          { Icon: MapIcon, title: t("pro.shops.f1_t"), text: t("pro.shops.f1_d") },
          { Icon: BookOpen, title: t("pro.shops.f2_t"), text: t("pro.shops.f2_d") },
          { Icon: BarChart3, title: t("pro.shops.f3_t"), text: t("pro.shops.f3_d") },
        ]}
        cta={t("pro.shops.cta")}
        imageUrl="https://customer-assets.emergentagent.com/job_geo-vinum-preview/artifacts/3m7qbj61_geovinumio-iframe.jpg"
      />

      <SegmentSection
        id="wine-schools"
        Icon={GraduationCap}
        eyebrow={t("pro.schools.title")}
        title={<>{t("pro.schools.headline_a")} <span className="italic text-[color:var(--gv-pink)]">{t("pro.schools.headline_b")}</span> {t("pro.schools.headline_c")}</>}
        desc={t("pro.schools.desc")}
        features={[
          { Icon: Users, title: t("pro.schools.f1_t"), text: t("pro.schools.f1_d") },
          { Icon: BookOpen, title: t("pro.schools.f2_t"), text: t("pro.schools.f2_d") },
          { Icon: Sparkles, title: t("pro.schools.f3_t"), text: t("pro.schools.f3_d") },
        ]}
        cta={t("pro.schools.cta")}
        imageSide="left"
        imageUrl="https://customer-assets-7cd3h4nn.emergentagent.net/job_geo-vinum-preview/artifacts/dm666mwh_Captura%20de%20Tela%202026-08-13%20a%CC%80s%2021.51.57.webp"
        bg
      />

      <SegmentSection
        id="professionals"
        Icon={Code2}
        eyebrow={t("pro.pros.title")}
        title={<>{t("pro.pros.headline_a")} <span className="italic text-[color:var(--gv-pink)]">{t("pro.pros.headline_b")}</span> {t("pro.pros.headline_c")}</>}
        desc={t("pro.pros.desc")}
        features={[
          { Icon: Monitor, title: t("pro.pros.f1_t"), text: t("pro.pros.f1_d") },
          { Icon: Code2, title: t("pro.pros.f2_t"), text: t("pro.pros.f2_d") },
          { Icon: Boxes, title: t("pro.pros.f3_t"), text: t("pro.pros.f3_d") },
          { Icon: Briefcase, title: t("pro.pros.f4_t"), text: t("pro.pros.f4_d") },
        ]}
        cta={t("pro.pros.cta")}
        imageUrl="https://customer-assets.emergentagent.com/job_geo-vinum-preview/artifacts/bpotwkdz_geovinumio-cartes-interactives.jpg"
      />

      {/* Value bar */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
            {[
              { Icon: Zap, text: t("pro.value.faster") },
              { Icon: CheckCircle2, text: t("pro.value.understand") },
              { Icon: Users, text: t("pro.value.teams") },
              { Icon: BarChart3, text: t("pro.value.sales") },
            ].map(({ Icon, text }, i) => (
              <div key={text} className="reveal bg-white border border-[color:var(--gv-line)] rounded-[5px] p-5" style={{ transitionDelay: `${i * 70}ms` }}>
                <Icon size={18} className="text-[color:var(--gv-pink)] mb-3" strokeWidth={1.6} />
                <div className="text-[14.5px] text-[color:var(--gv-ink)]/85">{text}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section id="work-with-us" className="scroll-mt-[152px] py-20 bg-[color:var(--gv-blue)] text-white relative overflow-hidden grain">
        <div className="relative max-w-3xl mx-auto px-6 lg:px-10 text-center reveal">
          <h3 className="font-display text-[32px] md:text-[46px] leading-[1.1] mb-6">
            {t("pro.cta")} <span aria-hidden>🍷</span>
          </h3>
          <p className="text-[15px] text-white/75 mb-8">
            {t("pro.cta_text")}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <a href="mailto:hello@geovinum.io?subject=geoVINUM for professionals" className="btn-primary" data-testid="pro-final-cta">
              {t("cta.contact_sales")} <ArrowRight size={16} />
            </a>
            <Link to="/maps" className="inline-flex items-center gap-2 px-5 py-3 rounded-[5px] border border-white/30 text-white hover:bg-white/10 transition-colors">
              {t("cta.explore_maps")}
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default ProPage;
